import React from 'react'

import classnames from 'classnames'
import startCase from 'lodash/startCase'

import { formatTimeString, FullSchedule, getToday } from 'src/lib/js/schedule'
import { t } from 'src/translations'

import Image from 'shared/components/common/Image'
import {
  Modal,
  ModalContent,
  ModalOverlay,
  useModal,
  ModalCloseButton
} from 'shared/components/common/modal'
import Popover from 'shared/components/common/popover/Popover'

import OpenStatus from 'public/components/default_template/footer/location/OpenStatus'

type ModalProps = {
  children?: (openModal: () => void) => React.ReactNode
} & AllHoursProps

type AllHoursProps = {
  isLocationOpen: boolean
  schedule: FullSchedule
  restaurantName: string
  timezoneId: string
}

const AllHoursModal = ({ children, ...allHoursProps }: ModalProps) => {
  const { isOpen, onClose, onOpen } = useModal()

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} testId='all-hours-modal'>
        <ModalOverlay fadeIn />
        <ModalContent
          fadeIn
          ariaLabelledBy={'allHoursModalRxName'}
          contentClassName='modalContentV2 fullScreenMobile'
        >
          <AllHours {...allHoursProps} />
        </ModalContent>
      </Modal>
      {children && children(onOpen)}
    </>
  )
}

const AllHours = ({
  restaurantName,
  isLocationOpen,
  schedule,
  timezoneId
}: AllHoursProps) => (
  <div className='allHoursModal'>
    <div className='modalHeader withHeaderIconRight'>
      <h3 id='allHoursModalRxName' className='rxName' tabIndex={-1}>
        {restaurantName}
      </h3>
      <ModalCloseButton className='iconRight' />
    </div>
    <div className='openStatus'>
      <OpenStatus
        isOpen={isLocationOpen}
        schedule={schedule}
        timezoneId={timezoneId}
      />
    </div>
    <div className='schedule'>
      {Object.entries(schedule).map(([day, daySchedule]) => {
        const isToday = day === getToday(timezoneId)
        return (
          <div key={day}>
            <span
              className={classnames('day', { bold: isLocationOpen && isToday })}
            >
              {startCase(day)}
            </span>
            <div className='hoursAndTooltip'>
              <div className='hoursWrapper'>
                {daySchedule.intervals.length
                  ? daySchedule.intervals.map((servicePeriod) => {
                      return (
                        <div
                          className={classnames('scheduleHours', {
                            bold: isLocationOpen && isToday
                          })}
                          key={formatTimeString(servicePeriod.startTime, true)}
                        >
                          {formatTimeString(servicePeriod.startTime, true)} -{' '}
                          {formatTimeString(servicePeriod.endTime, true)}
                        </div>
                      )
                    })
                  : t('all-hours-modal.closed')}
              </div>
              {daySchedule.overrideDescription && (
                <Popover
                  contextKey='scheduleOverrideTooltip'
                  arrowClassName='tooltipArrow'
                  target={({ open }) => (
                    <button
                      onClick={open}
                      aria-label={t('all-hours-modal.view-schedule-overrides')}
                    >
                      <Image
                        src='icons/info.svg'
                        alt=''
                        className='overrideTooltip'
                      />
                    </button>
                  )}
                >
                  <div className='tooltipContent'>
                    {daySchedule?.overrideDescription}
                  </div>
                </Popover>
              )}
            </div>
          </div>
        )
      })}
    </div>
  </div>
)
export default AllHoursModal
